/**
 * order-undo.ts — resolve the "return to previous status" target for a seller
 * order. Mirrors the backend policy: the linear fulfilment chain reverses
 * deterministically (no history, so undo can't ping-pong), while cancelled /
 * declined read the real previous status from statusHistory.
 */

import type { OrderStatus, SellerOrderDetail } from "@/lib/api/types";

const UNDO_CHAIN_TARGET: Partial<Record<OrderStatus, OrderStatus>> = {
  delivered: "shipped",
  shipped: "preparing",
  preparing: "confirmed",
};

const TERMINAL: ReadonlySet<OrderStatus> = new Set([
  "delivered",
  "cancelled",
  "declined",
]);

/** The status an undo would revert this order to, or null when not undoable. */
export function getUndoTarget(order: SellerOrderDetail): OrderStatus | null {
  const chainTarget = UNDO_CHAIN_TARGET[order.status];
  if (chainTarget) return chainTarget;

  if (order.status === "cancelled" || order.status === "declined") {
    const latest = [...(order.statusHistory ?? [])].sort(
      (a, b) =>
        new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime(),
    )[0];
    if (!latest) return null;
    const target = latest.oldStatus;
    if (target === "draft" || TERMINAL.has(target)) return null;
    return target;
  }

  return null;
}

/** What the undo unwinds — drives the confirmation copy. */
export type UndoKind = "eta" | "reason" | "plain";

export function getUndoKind(status: OrderStatus): UndoKind {
  if (status === "shipped") return "eta";
  if (status === "cancelled" || status === "declined") return "reason";
  return "plain";
}
